/**
 * LoadingSkeleton Component
 *
 * A skeleton placeholder component for content loading states.
 * Provides visual feedback while content is being loaded.
 */

import { type CSSProperties, type ReactNode } from 'react';

/**
 * Props for LoadingSkeleton component
 */
export interface LoadingSkeletonProps {
  /** Width of the skeleton (CSS value) */
  width?: string | number;
  /** Height of the skeleton (CSS value) */
  height?: string | number;
  /** Border radius (default: '4px') */
  borderRadius?: string | number;
  /** Variant type */
  variant?: 'text' | 'circular' | 'rectangular' | 'card';
  /** Number of skeleton lines for text variant */
  lines?: number;
  /** Animation type */
  animation?: 'pulse' | 'wave' | 'none';
  /** Additional CSS class name */
  className?: string;
  /** Additional inline styles */
  style?: CSSProperties;
}

/**
 * LoadingSkeleton displays a placeholder with animation during loading
 *
 * @example
 * ```tsx
 * <LoadingSkeleton width="100%" height="20px" />
 * <LoadingSkeleton variant="circular" width={50} height={50} />
 * <LoadingSkeleton variant="card" />
 * ```
 */
export const LoadingSkeleton = ({
  width,
  height,
  borderRadius,
  variant = 'rectangular',
  lines = 3,
  animation = 'pulse',
  className,
  style,
}: LoadingSkeletonProps) => {
  // Render multiple lines for text variant
  if (variant === 'text') {
    return (
      <div style={{ ...textContainerStyle, ...style }} className={className}>
        {Array.from({ length: lines }).map((_, index) => (
          <div
            key={index}
            style={{
              ...baseStyle,
              ...getAnimationStyle(animation),
              width: index === lines - 1 ? '60%' : '100%',
              height: height ?? '16px',
              borderRadius: borderRadius ?? '4px',
              marginBottom: index < lines - 1 ? '8px' : 0,
            }}
            role="presentation"
            aria-hidden="true"
          />
        ))}
      </div>
    );
  }

  // Render card skeleton
  if (variant === 'card') {
    return (
      <div style={{ ...cardContainerStyle, ...style }} className={className}>
        {/* Image placeholder */}
        <div
          style={{
            ...baseStyle,
            ...getAnimationStyle(animation),
            width: '100%',
            height: '150px',
            borderRadius: '8px 8px 0 0',
          }}
          role="presentation"
          aria-hidden="true"
        />
        {/* Content placeholders */}
        <div style={cardContentStyle}>
          <div
            style={{
              ...baseStyle,
              ...getAnimationStyle(animation),
              width: '70%',
              height: '20px',
              borderRadius: '4px',
              marginBottom: '8px',
            }}
            role="presentation"
            aria-hidden="true"
          />
          <div
            style={{
              ...baseStyle,
              ...getAnimationStyle(animation),
              width: '40%',
              height: '16px',
              borderRadius: '4px',
            }}
            role="presentation"
            aria-hidden="true"
          />
        </div>
      </div>
    );
  }

  // Get variant-specific styles
  const variantStyles = getVariantStyles(variant, width, height, borderRadius);

  return (
    <div
      style={{
        ...baseStyle,
        ...variantStyles,
        ...getAnimationStyle(animation),
        ...style,
      }}
      className={className}
      role="presentation"
      aria-hidden="true"
    />
  );
};

/**
 * Get styles based on variant
 */
const getVariantStyles = (
  variant: string,
  width?: string | number,
  height?: string | number,
  borderRadius?: string | number
): CSSProperties => {
  switch (variant) {
    case 'circular': {
      const size = width ?? height ?? 40;
      return {
        width: size,
        height: size,
        borderRadius: '50%',
      };
    }
    case 'rectangular':
    default:
      return {
        width: width ?? '100%',
        height: height ?? '20px',
        borderRadius: borderRadius ?? '4px',
      };
  }
};

/**
 * Get animation styles
 */
const getAnimationStyle = (animation: string): CSSProperties => {
  switch (animation) {
    case 'wave':
      return {
        background: 'linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%)',
        backgroundSize: '200% 100%',
        animation: 'wave 1.5s ease-in-out infinite',
      };
    case 'pulse':
      return {
        animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
      };
    case 'none':
    default:
      return {};
  }
};

/**
 * Base styles for skeleton
 */
const baseStyle: CSSProperties = {
  backgroundColor: '#e5e7eb',
  display: 'block',
};

/**
 * Container style for text variant
 */
const textContainerStyle: CSSProperties = {
  width: '100%',
};

/**
 * Container style for card variant
 */
const cardContainerStyle: CSSProperties = {
  backgroundColor: '#ffffff',
  borderRadius: '8px',
  overflow: 'hidden',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
};

/**
 * Content area style for card variant
 */
const cardContentStyle: CSSProperties = {
  padding: '12px',
};

/**
 * SkeletonProvider component that includes required CSS animations
 */
export const SkeletonStyles = (): ReactNode => {
  return (
    <style>
      {`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        @keyframes wave {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}
    </style>
  );
};

export default LoadingSkeleton;
